import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminSalesContentCreateAction,
  adminSalesContentGetAction,
  adminSalesContentUpdateAction
} from "../Action/action";

function normalizeCardsList(list) {
  if (!Array.isArray(list) || !list.length) return [{ title: "", description: "" }];
  return list.map((c) => ({ title: c.title || "", description: c.description || "" }));
}

function normalizeOutcomesList(list) {
  if (!Array.isArray(list) || !list.length) return [{ text: "" }];
  return list.map((o) => ({ text: typeof o === "string" ? o : o?.text || "" }));
}

function isCardFilled(card) {
  return Boolean(String(card?.title || "").trim() && String(card?.description || "").trim());
}

function isOutcomeFilled(item) {
  return Boolean(String(item?.text || "").trim());
}

export default function SalesContentForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    heading_title: "Sales",
    brand_title: "Brave Brain Academy",
    tagline: "Brave Inside. Bold Outside",
    hero_lead: "",
    hero_sub: "",
    enrol_button_label: "Enrol Now",
    book_call_button_label: "Book a Free Call First",
    website_button_label: "More Info on Our Website",
    inside_heading: "Inside Brave Brain Academy You'll Receive:",
    inside_cards: [{ title: "", description: "" }],
    transformation_heading: "The Transformation Begins Here...",
    transformation_intro: "",
    transformation_outcomes: [{ text: "" }],
    transformation_quote: "",
    invest_heading: "",
    invest_closing: "",
    footer_enrol_label: "Enrol Now",
    status: 1
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminSalesContentGetAction(id);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          toast.error(res?.message || "Not found", { toastId: "sales-form" });
          navigate("/sales-content");
          return;
        }
        const d = res.data;
        setForm({
          heading_title: d.heading_title || "",
          brand_title: d.brand_title || "",
          tagline: d.tagline || "",
          hero_lead: d.hero_lead || "",
          hero_sub: d.hero_sub || "",
          enrol_button_label: d.enrol_button_label || "",
          book_call_button_label: d.book_call_button_label || "",
          website_button_label: d.website_button_label || "",
          inside_heading: d.inside_heading || "",
          inside_cards: normalizeCardsList(d.inside_cards),
          transformation_heading: d.transformation_heading || "",
          transformation_intro: d.transformation_intro || "",
          transformation_outcomes: normalizeOutcomesList(d.transformation_outcomes),
          transformation_quote: d.transformation_quote || "",
          invest_heading: d.invest_heading || "",
          invest_closing: d.invest_closing || "",
          footer_enrol_label: d.footer_enrol_label || "",
          status: Number(d.status) === 0 ? 0 : 1
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to load", {
          toastId: "sales-form"
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id, navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setCard = (index, key, value) => {
    setForm((prev) => {
      const cards = [...prev.inside_cards];
      cards[index] = { ...cards[index], [key]: value };
      return { ...prev, inside_cards: cards };
    });
  };

  const addCard = () => {
    const cards = form.inside_cards || [];
    const last = cards[cards.length - 1];
    if (cards.length && !isCardFilled(last)) {
      toast.error("Fill the previous card title and description before adding another.", {
        toastId: "sales-form"
      });
      return;
    }
    setForm((prev) => ({
      ...prev,
      inside_cards: [...prev.inside_cards, { title: "", description: "" }]
    }));
  };

  const removeCard = (index) => {
    setForm((prev) => {
      const cards = prev.inside_cards.filter((_, i) => i !== index);
      return {
        ...prev,
        inside_cards: cards.length ? cards : [{ title: "", description: "" }]
      };
    });
  };

  const setOutcome = (index, value) => {
    setForm((prev) => {
      const items = [...prev.transformation_outcomes];
      items[index] = { text: value };
      return { ...prev, transformation_outcomes: items };
    });
  };

  const addOutcome = () => {
    const items = form.transformation_outcomes || [];
    const last = items[items.length - 1];
    if (items.length && !isOutcomeFilled(last)) {
      toast.error("Fill the previous outcome before adding another.", { toastId: "sales-form" });
      return;
    }
    setForm((prev) => ({
      ...prev,
      transformation_outcomes: [...prev.transformation_outcomes, { text: "" }]
    }));
  };

  const removeOutcome = (index) => {
    setForm((prev) => {
      const items = prev.transformation_outcomes.filter((_, i) => i !== index);
      return {
        ...prev,
        transformation_outcomes: items.length ? items : [{ text: "" }]
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.brand_title || "").trim()) {
      toast.error("Brand title is required", { toastId: "sales-form" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heading_title: String(form.heading_title || "").trim() || null,
        brand_title: String(form.brand_title || "").trim(),
        tagline: form.tagline != null ? String(form.tagline) : "",
        hero_lead: form.hero_lead != null ? String(form.hero_lead) : "",
        hero_sub: form.hero_sub != null ? String(form.hero_sub) : "",
        enrol_button_label: form.enrol_button_label != null ? String(form.enrol_button_label) : "",
        book_call_button_label:
          form.book_call_button_label != null ? String(form.book_call_button_label) : "",
        website_button_label:
          form.website_button_label != null ? String(form.website_button_label) : "",
        inside_heading: form.inside_heading != null ? String(form.inside_heading) : "",
        transformation_heading:
          form.transformation_heading != null ? String(form.transformation_heading) : "",
        transformation_intro:
          form.transformation_intro != null ? String(form.transformation_intro) : "",
        transformation_quote:
          form.transformation_quote != null ? String(form.transformation_quote) : "",
        invest_heading: form.invest_heading != null ? String(form.invest_heading) : "",
        invest_closing: form.invest_closing != null ? String(form.invest_closing) : "",
        footer_enrol_label: form.footer_enrol_label != null ? String(form.footer_enrol_label) : "",
        status: Number(form.status) === 1 ? 1 : 0,
        inside_cards: (form.inside_cards || []).filter((c) => isCardFilled(c)),
        transformation_outcomes: (form.transformation_outcomes || []).filter((o) =>
          isOutcomeFilled(o)
        )
      };

      const res = isEdit
        ? await adminSalesContentUpdateAction(id, payload)
        : await adminSalesContentCreateAction(payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Updated" : "Created", { toastId: "sales-form" });
      navigate("/sales-content");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "sales-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  const canAddCard = isCardFilled(form.inside_cards[form.inside_cards.length - 1]);
  const canAddOutcome = isOutcomeFilled(
    form.transformation_outcomes[form.transformation_outcomes.length - 1]
  );

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/sales-content">Sales Content</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to="/sales-content">
          ← Back
        </Link>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card mb-6">
          <h2 className="section-title">1. Hero</h2>
          <div className="form-group">
            <label>Heading title</label>
            <input
              className="form-control"
              value={form.heading_title}
              onChange={(e) => setField("heading_title", e.target.value)}
              placeholder="Sales"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                Brand title <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={form.brand_title}
                onChange={(e) => setField("brand_title", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Tagline</label>
              <input
                className="form-control"
                value={form.tagline}
                onChange={(e) => setField("tagline", e.target.value)}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Lead text</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.hero_lead}
              onChange={(e) => setField("hero_lead", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Supporting text</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.hero_sub}
              onChange={(e) => setField("hero_sub", e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Enrol button label</label>
              <input
                className="form-control"
                value={form.enrol_button_label}
                onChange={(e) => setField("enrol_button_label", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Book call button</label>
              <input
                className="form-control"
                value={form.book_call_button_label}
                onChange={(e) => setField("book_call_button_label", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Website button</label>
              <input
                className="form-control"
                value={form.website_button_label}
                onChange={(e) => setField("website_button_label", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <h2 className="section-title">2. Inside Brave Brain Academy</h2>
          <div className="form-group">
            <label>Section heading</label>
            <input
              className="form-control"
              value={form.inside_heading}
              onChange={(e) => setField("inside_heading", e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 8
              }}
            >
              <label style={{ margin: 0, fontWeight: 600 }}>Cards</label>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={addCard}
                disabled={!canAddCard}
                title={
                  canAddCard
                    ? "Add card"
                    : "Fill previous card title and description first"
                }
                style={{ minWidth: 36 }}
              >
                +
              </button>
            </div>
            {form.inside_cards.map((card, idx) => (
              <div
                key={`card-${idx}`}
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  alignItems: "flex-end",
                  gap: 8,
                  marginBottom: 12
                }}
              >
                <div className="form-group" style={{ flex: "1 1 0", minWidth: 0, marginBottom: 0 }}>
                  <label>Card {idx + 1} title</label>
                  <input
                    className="form-control"
                    value={card.title}
                    onChange={(e) => setCard(idx, "title", e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ flex: "2 1 0", minWidth: 0, marginBottom: 0 }}>
                  <label>Card {idx + 1} description</label>
                  <input
                    className="form-control"
                    value={card.description}
                    onChange={(e) => setCard(idx, "description", e.target.value)}
                  />
                </div>
                <div style={{ flex: "0 0 auto", paddingBottom: 2 }}>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm text-danger"
                    onClick={() => removeCard(idx)}
                    disabled={form.inside_cards.length <= 1}
                    title="Remove card"
                    style={{ minWidth: 36 }}
                  >
                    −
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card mb-6">
          <h2 className="section-title">3. Transformation</h2>
          <div className="form-group">
            <label>Heading</label>
            <input
              className="form-control"
              value={form.transformation_heading}
              onChange={(e) => setField("transformation_heading", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Intro</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.transformation_intro}
              onChange={(e) => setField("transformation_intro", e.target.value)}
            />
          </div>
          <div className="form-group">
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 8
              }}
            >
              <label style={{ margin: 0, fontWeight: 600 }}>Outcomes</label>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={addOutcome}
                disabled={!canAddOutcome}
                title={canAddOutcome ? "Add outcome" : "Fill previous outcome first"}
                style={{ minWidth: 36 }}
              >
                +
              </button>
            </div>
            {form.transformation_outcomes.map((item, idx) => (
              <div
                key={`out-${idx}`}
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  alignItems: "flex-end",
                  gap: 8,
                  marginBottom: 12
                }}
              >
                <div className="form-group" style={{ flex: "1 1 0", minWidth: 0, marginBottom: 0 }}>
                  <label>Outcome {idx + 1}</label>
                  <input
                    className="form-control"
                    value={item.text}
                    onChange={(e) => setOutcome(idx, e.target.value)}
                  />
                </div>
                <div style={{ flex: "0 0 auto", paddingBottom: 2 }}>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm text-danger"
                    onClick={() => removeOutcome(idx)}
                    disabled={form.transformation_outcomes.length <= 1}
                    title="Remove outcome"
                    style={{ minWidth: 36 }}
                  >
                    −
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="form-group">
            <label>Side quote</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.transformation_quote}
              onChange={(e) => setField("transformation_quote", e.target.value)}
            />
          </div>
        </div>

        <div className="card mb-6">
          <h2 className="section-title">4. Footer / investment CTA</h2>
          <div className="form-group">
            <label>Investment heading</label>
            <input
              className="form-control"
              value={form.invest_heading}
              onChange={(e) => setField("invest_heading", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Closing statement</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.invest_closing}
              onChange={(e) => setField("invest_closing", e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Footer enrol button</label>
              <input
                className="form-control"
                value={form.footer_enrol_label}
                onChange={(e) => setField("footer_enrol_label", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to="/sales-content">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
